import { isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

export const localDateTimeInTimeZone = (value?: string | number | Date, timezone?: string) => {
  const zonedDateTime = value && isValid(new Date(value)) ? new Date(value) : new Date();
  return new Date(
    formatInTimeZone(
      zonedDateTime,
      timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      "yyyy-MM-dd HH:mm:ss"
    )
  );
};

export const timeAgo = (date: string | number | Date) => {
  const now = new Date().getTime();
  const then = new Date(date).getTime();
  const seconds = Math.floor((now - then) / 1000);

  const intervals: Record<string, number> = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  };

  for (const key in intervals) {
    const value = Math.floor(seconds / intervals[key]);
    if (value >= 1) {
      return `${value} ${key}${value > 1 ? "s" : ""} ago`;
    }
  }
  return "Just now";
};

export const timeAgoShort = (date: string | number | Date) => {
  const now = new Date().getTime();
  const then = new Date(date).getTime();
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) return "Just now";

  const minute = 60;
  const hour = 3600;
  const day = 86400;
  const week = 604800;
  const month = 2592000; // approx
  const year = 31536000; // approx

  if (seconds < hour) {
    const m = Math.floor(seconds / minute);
    return `${m}m`;
  }
  if (seconds < day) {
    const h = Math.floor(seconds / hour);
    return `${h}h`;
  }
  if (seconds < week) {
    const d = Math.floor(seconds / day);
    return `${d}d`;
  }
  if (seconds < month) {
    const w = Math.floor(seconds / week);
    return `${w}w`;
  }
  if (seconds < year) {
    const mo = Math.floor(seconds / month);
    return `${mo}mo`;
  }

  const y = Math.floor(seconds / year);
  return `${y}y`;
};

export const sanitizeFileName = (filename: string) => {
  const lastDot = filename.lastIndexOf(".");
  const name = filename.slice(0, lastDot);
  const ext = filename.slice(lastDot);
  const sanitized = name
    .normalize("NFKD")
    .replace(/[^\w\s.-]/g, "")
    .replace(/\./g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^\.+/, "")
    .trim()
    .toLowerCase();
  return sanitized + ext.toLowerCase();
};

export const downloadImage = (url: string, filename: string) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const base64ToFile = (base64String: string, filename: string) => {
  const arr = base64String.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

export const urlToFile = async (url, preferredName?: string) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();

    let filename;
    const disposition = res.headers.get("content-disposition");
    if (disposition) {
      const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
      filename = match ? decodeURIComponent(match[1]) : null;
    }

    if (!filename) {
      const urlObj = new URL(url);
      const path = urlObj.pathname;
      filename = path.substring(path.lastIndexOf("/") + 1);
    }

    if (!filename) {
      filename = "image.png";
    }

    // Use preferredName (e.g. Canva design title) if provided, keep extension from original
    if (preferredName) {
      const ext = filename.substring(filename.lastIndexOf(".")) || ".png";
      filename = sanitizeFileName(`${preferredName}${ext}`);
    } else {
      filename = sanitizeFileName(`${Date.now()}-${filename}`);
    }

    return {
      file: new File([blob], filename, { type: blob.type }),
      filename: filename,
    };
  } catch {
    return {
      file: null,
      filename: null,
    };
  }
};

export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};
